import * as d3 from 'd3-selection'
import { interpolate } from 'd3-interpolate'
import Transformer from './transformer'
import { PROJECTIONS} from '..'
import { FEATURES } from '..'
import { scaleLinear as scale } from 'd3-scale'
import { easeCubic as ease } from 'd3-ease'
import * as geo from 'd3-geo'
import inverseEaseCubic from '../inverse-ease-cubic'
import Decorator from './decorator'

const geoGraticule = geo.geoGraticule()
const WORLD = FEATURES.WORLD

const epsilon = 1e-6

const oneThird = 1 / 3

const interpolatedProjection = (map, globe) => {
  let alpha
    , clip = Math.PI
    , projection = geo.geoProjection(raw)

  projection.preclip(stream => {
              stream = geo.geoClipAntimeridian(stream)
              return geo.geoClipCircle(clip)(stream)
            })
            .scale(1)

  let center = projection.center
    , translate = projection.translate

  function raw (lambda, phi) {
    let pa = map([lambda *= 180 / Math.PI, phi *= 180 / Math.PI])
      , pb = globe([lambda, phi])

    if (alpha > 0.4) {
      clip = Math.PI / 2 + (0.60 - (alpha - 0.4)) * Math.PI / 2
    } else {
      clip = Math.PI
    }

    return [(1 - alpha) * pa[0] + alpha * pb[0], (alpha - 1) * pa[1] - alpha * pb[1]]
  }

  projection.alpha = function (t) {
    if (!arguments.length) {
      return alpha
    }

    alpha = t

    let center0 = map.center()
      , center1 = globe.center()
      , translate0 = map.translate()
      , translate1 = globe.translate()

    center([(1 - alpha) * center0[0] + alpha * center1[0], (1 - alpha) * center0[1] + alpha * center1[1]])
    translate([(1 - alpha) * translate0[0] + alpha * translate1[0], (1 - alpha) * translate0[1] + alpha * translate1[1]])

    return projection
  }

  delete projection.scale
  delete projection.translate

  return projection
}

/*
 * This will toggle a world map between 2d and 3d.
 * This transformer is used when we're not removing a previous feature. In
 * other words, the map is just being updated between 2d and 3d dimensions.
 */
class DimensionTransformer extends Transformer {

  static match (d0, d1, opts) {
    if (d0) {
      // This doesn't handle loading new features, only world <-> world
      return false
    }
    return d0.globe !== d1.globe ||
          (opts.container.classed('globe') && !d1.globe) // Currently a globe, next transition will always transform to 2d
  }

  constructor (enter, exit, opts) {
    super(enter, exit, opts)
    this.enter = enter
    this.container = opts.container

    let globe = PROJECTIONS.globe().fitSize([opts.width, opts.height], {type: 'Sphere'})
      , map = PROJECTIONS.map().fitSize([opts.width, opts.height], enter.datum().geojson)

    // Store the path and projections
    let data = enter.datum()
      , rotate = opts.rotate || (data.projection ? data.projection.rotate() : [0, 0, 0])

    this._noop = this.container.classed('globe') && opts.globe // no-op transform if we're already a globe and this matched

    this.container.classed('globe', opts.globe) // Turning into a globe

    data.projection = opts.globe ? globe : map
    data.path = geo.geoPath().projection(data.projection)

    this.interpolatedProjection = interpolatedProjection(map, globe)
    this.interpolatedPath = geo.geoPath().projection(this.interpolatedProjection)
    this.interpolatedProjection.alpha(0)

    this.container = opts.container
    this.globe = globe

    if (rotate.some(Boolean)) {
      // The globe was rotated, we'll use a two part animation that rotates
      // the globe to [0, 0, 0], before changing the projection to a map
      this.scales = {
        rotate: scale().domain([0, oneThird]) // First half of the transition
                       .range([1, 0])
                       .clamp(true),
        wrap: scale().domain([oneThird, 1]) // 2nd half of the transition
                     .range([1, 0])
                     .clamp(true)
      }
      let mapRotate = map.rotate()
      if (rotate[0] > 180) {
        mapRotate[0] = 360  // Change the unwind direction
      }
      this.rotateInterpolater = interpolate(mapRotate, rotate)
    }

    if (opts.globe) {
      this._decorate() // Add the decorations
    }

    this.decorator = new Decorator({
      ee: opts.ee,
      chart: opts.chart,
      container: opts.container,
      projection: this.interpolatedProjection,
      globeProjection: globe,
      path: data.path
    })
  }

  _decorate () {
    // Currently a map, add the decorations

    // Add graticule below features
    this.container
        .selectAll('.graticule')
        .data(d => [d])
        .enter()
        .insert('path', ':first-child')
        .attr('class', 'graticule')
        .merge(this.container.selectAll('.graticule'))
        .data([geoGraticule()])

    // Now add globe outline below graticule, so graticule is on top
    this.container
        .selectAll('.globe-outline')
        .data(d => [d])
        .enter()
        .insert('path', ':first-child')
        .attr('class', 'globe-outline')
  }

  _project (t) {
    this.interpolatedProjection.alpha(t)
    this.enter.selectAll('path.feature').attr('d', this.interpolatedPath)
    this.decorator.transform(t)
  }

  /*
   * Two part animation is used when the globe is rotated
   */
  _twoPart (t) {
    let elapsed = inverseEaseCubic(t) // Undo the easing on this transition
    if (elapsed < oneThird) {
      let rotateT = this.scales.rotate(elapsed)
        , path = geo.geoPath().projection(this.globe)

      this.globe.rotate(this.rotateInterpolater(rotateT))
      this.enter
          .selectAll('path.feature')
          .attr('d', path)
      this.container.selectAll('.graticule').attr('d', path)
    } else {
      t = ease(this.scales.wrap(elapsed))
      this._project(t)
    }
  }

  /*
   * By default this transformer is set up to transform from a map
   * to a globe. We receive the current time, `t`, of the transition.
   *
   * The map transitions use the default d3 transition, which is a cubic function.
   * When we're transitioning to a map, we use a two part animation, which rotates
   * the globe to the origin [0,0,0], to fix a lot of bugs with clipping paths
   * and making the globe -> map transition look better.
   */
  transform (t) {
    if (this._noop) {
      return // no-op
    }

    if (this.twoPart) {
      return this._twoPart(t)
    }

    if (!this.container.classed('globe')) {
      t = 1 - t // reverse transition
    }
    this._project(t)
    this.decorator.transform(t)
  }

  end () {
    if (!this.container.classed('globe')) {
      // Remove the globe decorations
      this.container.selectAll('.globe-outline, .graticule').remove()
    } else if (!this._noop) {
      this.decorator.startRotation()
    }
  }

}

export default DimensionTransformer
